import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Column,
  EmptyState,
  Icon,
  IconButton,
  PageHeader,
  Skeleton,
  Table,
  TextField,
  styles as ui,
  useConfirm,
} from "stories/Admin";
import { useAuth } from "AuthProvider";
import useFetch from "hooks/useFetch";
import styles from "./ApiPage.module.scss";

/*
 * API for moskeen: admin lager API-nøkler som brukes mot iBønns åpne API, så moskeen kan vise
 * egne bønnetider, jamat og beskjeder på nettsiden sin, infoskjermer o.l.
 */

export interface ApiKeyOrigin {
  origin: string;
  requests: number;
  lastUsedAt: string;
  lastIp?: string | null;
  lastUserAgent?: string | null;
  lastPath?: string | null;
}

export interface ApiKey {
  id: number;
  orgNr: string;
  mosqueName?: string | null;
  name: string;
  keyPrefix: string;
  createdByEmail?: string | null;
  createdAt: string;
  revokedAt?: string | null;
  lastUsedAt?: string | null;
  usageCount: number;
  origins: ApiKeyOrigin[];
}

export const API_BASE = "https://api.xn--ibnn-hra.no/api/public/v1";

export const formatDateTime = (value?: string | null) =>
  value
    ? new Date(value).toLocaleString("nb-NO", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "–";

const ApiPage: React.FC = () => {
  const { selectedOrgNumber, selectedOrgName, mosqueName } = useAuth();
  const { execute } = useFetch<any>();
  const executeRef = useRef(execute);
  executeRef.current = execute;

  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState<ApiKey | null>(null);
  const confirm = useConfirm();
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!selectedOrgNumber) return;
    setIsLoading(true);
    const result = await executeRef.current(
      `/api/v1/mosques/${selectedOrgNumber}/api-keys`,
      "GET",
    );
    setKeys(Array.isArray(result) ? result : []);
    setIsLoading(false);
  }, [selectedOrgNumber]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    setCreating(true);
    setError(null);
    const result = await executeRef.current(
      `/api/v1/mosques/${selectedOrgNumber}/api-keys`,
      "POST",
      {
        name: name.trim() || "Nettsiden",
      },
    );
    setCreating(false);
    if (result?.key) {
      setNewKey(result.key);
      setCopied(false);
      setName("");
      load();
    } else {
      setError("Kunne ikke lage nøkkelen. Prøv igjen.");
    }
  };

  const revoke = async (target: ApiKey | null = revoking) => {
    if (!target) return;
    await executeRef.current(
      `/api/v1/mosques/${selectedOrgNumber}/api-keys/${target.id}`,
      "DELETE",
    );
    setRevoking(null);
    load();
  };

  const askRevoke = async (key: ApiKey) => {
    setRevoking(key);
    const ok = await confirm({
      title: `Trekke tilbake «${key.name}»?`,
      message:
        "Alt som bruker denne nøkkelen slutter å virke med en gang. Dette kan ikke angres.",
      confirmLabel: "Trekk tilbake",
      tone: "danger",
    });
    if (ok) {
      await revoke(key);
    } else {
      setRevoking(null);
    }
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const shownMosque = selectedOrgName || mosqueName || "moskeen";
  const exampleKey = newKey || "DIN_API_NØKKEL";
  const jsExample = `fetch("${API_BASE}/prayer-times", {
  headers: { "X-Api-Key": "${exampleKey}" }
})
  .then((r) => r.json())
  .then((data) => {
    const today = data.days[0];
    console.log(today.salah.fajr, today.jamat.fajr);
  });`;

  const columns: Array<Column<ApiKey>> = [
    {
      key: "name",
      header: "Navn",
      render: (k) => (
        <div className={styles.nameCell}>
          <b>{k.name}</b>
          {k.revokedAt && (
            <Badge tone="grey">
              Trukket tilbake {formatDateTime(k.revokedAt)}
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: "key",
      header: "Nøkkel",
      render: (k) => <code className={styles.inlineCode}>{k.keyPrefix}…</code>,
    },
    {
      key: "created",
      header: "Laget",
      render: (k) => (
        <div>
          <div className={ui.num}>{formatDateTime(k.createdAt)}</div>
          <div className={styles.muted}>{k.createdByEmail || ""}</div>
        </div>
      ),
    },
    {
      key: "lastUsed",
      header: "Sist brukt",
      numeric: true,
      render: (k) => formatDateTime(k.lastUsedAt),
    },
    {
      key: "calls",
      header: "Kall",
      numeric: true,
      align: "right",
      render: (k) => k.usageCount.toLocaleString("nb-NO"),
    },
    {
      key: "origins",
      header: "Brukt fra (30 dager)",
      render: (k) =>
        k.origins.length === 0 ? (
          <span className={styles.muted}>–</span>
        ) : (
          <div className={styles.origins}>
            {k.origins.slice(0, 3).map((o) => (
              <div key={o.origin}>
                {o.origin}{" "}
                <span className={styles.muted}>
                  ({o.requests.toLocaleString("nb-NO")})
                </span>
              </div>
            ))}
          </div>
        ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (k) =>
        !k.revokedAt && (
          <Button size="sm" variant="danger" onClick={() => askRevoke(k)}>
            Trekk tilbake
          </Button>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title="API"
        subtitle={`Vis bønnetidene til ${shownMosque} på deres egen nettside, infoskjerm eller i andre systemer – alltid oppdatert med det dere legger inn i iBønn.`}
      />

      <div className={ui.g3}>
        {(
          [
            [
              "clock",
              "Bønnetider",
              "Salah-tidene for moskeen, for én dag eller opptil 31 dager.",
            ],
            [
              "people",
              "Jamat",
              "Jamat-tidene dere legger inn i iBønn, samme dag som bønnetidene.",
            ],
            [
              "mosque",
              "Info fra moskeen",
              "Adresse, kontakt, jummah og aktive beskjeder.",
            ],
          ] as const
        ).map(([icon, title, text]) => (
          <div key={title} className={styles.feature}>
            <span className={styles.featureIcon} aria-hidden="true">
              <Icon name={icon} size={18} />
            </span>
            <span className={styles.featureText}>
              <b>{title}</b>
              <span className={styles.muted}>{text}</span>
            </span>
          </div>
        ))}
      </div>

      <Card title="Slik fungerer det">
        <ol className={styles.steps}>
          <li>
            Lag en API-nøkkel her. Gi den et navn, f.eks. «Nettsiden» eller
            «Infoskjerm i resepsjonen».
          </li>
          <li>Kopier nøkkelen – den vises bare én gang.</li>
          <li>
            Send nøkkelen i headeren{" "}
            <code className={styles.inlineCode}>X-Api-Key</code> når dere henter
            data fra API-et.
          </li>
          <li>
            Ser dere at en nøkkel brukes et sted den ikke skal, trekker dere den
            tilbake her.
          </li>
        </ol>
        <div className={styles.createRow}>
          <TextField
            label="Navn på nøkkelen"
            placeholder="F.eks. Nettsiden"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            fieldClassName={styles.createName}
          />
          <Button
            onClick={create}
            loading={creating}
            disabled={!selectedOrgNumber}
            icon={<Icon name="key" size={16} strokeWidth={2} />}
          >
            Lag ny API-nøkkel
          </Button>
        </div>
        {error && <Alert tone="error">{error}</Alert>}
      </Card>

      <Card title="Nøkler">
        {isLoading ? (
          <div className={styles.skeletons}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} height={36} />
            ))}
          </div>
        ) : (
          <Table
            columns={columns}
            rows={keys}
            rowKey={(k) => k.id}
            rowState={(k) => (k.revokedAt ? "dim" : undefined)}
            caption="API-nøkler"
            empty={
              <EmptyState icon="key" title="Ingen nøkler ennå">
                Lag en nøkkel over for å komme i gang.
              </EmptyState>
            }
          />
        )}
      </Card>

      <Card title="Dokumentasjon">
        <div className={styles.docBlock}>
          <b>Bønnetider og jamat</b>
          <span className={styles.muted}>
            <code className={styles.inlineCode}>
              GET {API_BASE}/prayer-times
            </code>{" "}
            – i dag. Én dag:{" "}
            <code className={styles.inlineCode}>?date=2026-10-12</code>. Periode
            (maks 31 dager):{" "}
            <code className={styles.inlineCode}>
              ?from=2026-10-01&amp;to=2026-10-31
            </code>
            .
          </span>
          <pre className={styles.code}>{`{
  "mosque": "${shownMosque}",
  "prayerTimeTable": "Standard",
  "days": [
    {
      "date": "2026-10-12",
      "hijriDate": "1-5-1448",
      "salah": { "fajr": "05:45", "sunrise": "07:47", "dhuhr": "13:08", "asr": "15:32", "maghrib": "18:22", "isha": "20:11" },
      "jamat": { "fajr": "06:15", "dhuhr": "14:00", "asr": "15:42", "maghrib": null, "isha": "20:16" }
    }
  ]
}`}</pre>
        </div>
        <div className={styles.docBlock}>
          <b>Info om moskeen</b>
          <span className={styles.muted}>
            <code className={styles.inlineCode}>GET {API_BASE}/mosque</code> –
            navn, adresse, kontakt, nettside, jummah og aktive beskjeder.
          </span>
        </div>
        <div className={styles.docBlock}>
          <b>Eksempel for nettsiden (JavaScript)</b>
          <pre className={styles.code}>{jsExample}</pre>
        </div>
        <div className={styles.docBlock}>
          <b>Eksempel med curl</b>
          <pre
            className={styles.code}
          >{`curl -H "X-Api-Key: ${exampleKey}" ${API_BASE}/prayer-times`}</pre>
        </div>
        <span className={styles.muted}>
          Grense: {(2000).toLocaleString("nb-NO")} kall i timen per nøkkel.
          Nøkkelen kan også sendes som{" "}
          <code className={styles.inlineCode}>?api_key=…</code> der headere ikke
          er mulig. Alle kall logges med dato og hvilket domene de kom fra.
        </span>
      </Card>

      {newKey && (
        <div
          className={ui.overlay}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setNewKey(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") setNewKey(null);
          }}
        >
          <div
            className={ui.modal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-key-title"
          >
            <h2 className={ui.mh} id="new-key-title">
              <span className={ui.mhIcon} aria-hidden="true">
                <Icon name="key" size={18} />
              </span>
              Ny API-nøkkel
              <IconButton
                className={styles.modalClose}
                label="Lukk"
                icon={<Icon name="x" size={18} strokeWidth={2} />}
                onClick={() => setNewKey(null)}
              />
            </h2>
            <div className={ui.mb}>
              <Alert tone="warn">
                Kopier nøkkelen nå. Den vises bare denne ene gangen.
              </Alert>
              <pre className={`${styles.code} ${styles.keyCode}`}>{newKey}</pre>
            </div>
            <div className={ui.mf}>
              <Button variant="secondary" onClick={() => setNewKey(null)}>
                Lukk
              </Button>
              <Button
                onClick={() => newKey && copy(newKey)}
                icon={
                  <Icon
                    name={copied ? "check" : "copy"}
                    size={16}
                    strokeWidth={2}
                  />
                }
                autoFocus
              >
                {copied ? "Kopiert" : "Kopier nøkkelen"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ApiPage;
